import React, { useState } from "react";
import { CaretLeft, Trash, Warning, Check, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listClearableSurfaces,
  clearSurface,
  clearAllSurfaces,
} from "../../lib/clearHistory";
import { confirmDelete } from "../../lib/confirmDelete";

/**
 * Settings → "Clear data". One row per history surface, plus a single
 * red "Clear everything" CTA at the bottom that wipes them all after a
 * native confirm. Voice/account preferences are intentionally NOT
 * touched — only USER-GENERATED HISTORY.
 *
 * Per-row clearing is also confirmed via window.confirm so a stray tap
 * can never destroy data. Each row shows a green checkmark for 2s
 * after a successful clear so the user knows it worked.
 */
export default function ClearDataManager({ onBack }) {
  const surfaces = listClearableSurfaces();
  const [clearing, setClearing] = useState(null);
  const [cleared, setCleared] = useState({});
  const [clearingAll, setClearingAll] = useState(false);

  async function handleClear(s) {
    const ok = await confirmDelete({
      title: `Clear ${s.label.toLowerCase()}?`,
      subtext: "Deleted items can't be undone.",
      confirmLabel: "Clear",
    });
    if (!ok) return;
    setClearing(s.id);
    try {
      const success = await clearSurface(s.id);
      if (success) {
        setCleared((prev) => ({ ...prev, [s.id]: Date.now() }));
        toast.success(`${s.label} cleared`);
        setTimeout(() => {
          setCleared((prev) => {
            const next = { ...prev };
            delete next[s.id];
            return next;
          });
        }, 2200);
      } else {
        toast.error(`Couldn't clear ${s.label.toLowerCase()}. Try again.`);
      }
    } finally {
      setClearing(null);
    }
  }

  async function handleClearAll() {
    const ok = await confirmDelete({
      title: "Clear ALL local history?",
      subtext:
        "Wipes translations, currency conversions, phrasebook, saved tips, and lost-luggage drafts on this device. Account, subscription, and voice settings are NOT affected. Deleted items can't be undone.",
      confirmLabel: "Clear everything",
    });
    if (!ok) return;
    setClearingAll(true);
    try {
      const { ok: allOk, errors } = await clearAllSurfaces();
      if (allOk) {
        toast.success("All local history cleared");
      } else {
        toast.error(`Cleared with ${errors.length} issue(s). Try again.`);
      }
      // Mark every surface as cleared in the UI so the user gets visual
      // confirmation across the entire list.
      const now = Date.now();
      const all = {};
      for (const s of surfaces) all[s.id] = now;
      setCleared(all);
      setTimeout(() => setCleared({}), 2500);
    } finally {
      setClearingAll(false);
    }
  }

  return (
    <div className="min-h-screen bg-bone pb-24" data-testid="clear-data-page">
      <div className="sticky top-0 z-10 bg-bone/95 backdrop-blur-sm border-b border-navy/10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="clear-data-back-btn"
            className="w-9 h-9 rounded-full bg-white border border-navy/10 flex items-center justify-center"
          >
            <CaretLeft size={18} weight="bold" color="#0E2C54" />
          </button>
          <h1 className="font-jakarta font-extrabold text-navy text-xl">
            Clear data
          </h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 space-y-5">
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl bg-orange/10 border border-orange/20">
          <Warning size={16} weight="duotone" color="#E55B2C" className="shrink-0 mt-0.5" />
          <span className="font-jakarta text-[12px] text-navy leading-snug">
            Clearing affects this device only. Your account, subscription,
            and voice settings are <span className="font-bold">never</span>{" "}
            touched.
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-navy/5 divide-y divide-navy/5">
          {surfaces.map((s) => {
            const isBusy = clearing === s.id || clearingAll;
            const isDone = !!cleared[s.id];
            return (
              <div
                key={s.id}
                className="flex items-center justify-between p-4 gap-3"
                data-testid={`clear-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-jakarta font-extrabold text-navy text-base">
                    {s.label}
                  </div>
                  <div className="font-jakarta text-navy-soft text-[13px] mt-0.5 leading-snug">
                    {s.description}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleClear(s)}
                  disabled={isBusy}
                  data-testid={`clear-btn-${s.id}`}
                  aria-label={`Clear ${s.label}`}
                  className={`h-9 px-3 rounded-full border flex items-center gap-1.5 shrink-0 transition-colors ${
                    isDone
                      ? "bg-forest border-forest text-white"
                      : "bg-white border-navy/15 text-navy active:bg-bone"
                  } disabled:opacity-50`}
                >
                  {isBusy ? (
                    <CircleNotch size={14} weight="bold" className="animate-spin" />
                  ) : isDone ? (
                    <Check size={14} weight="bold" />
                  ) : (
                    <Trash size={14} weight="duotone" />
                  )}
                  <span className="font-jakarta font-bold text-xs">
                    {isDone ? "Cleared" : "Clear"}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={handleClearAll}
          disabled={clearingAll || clearing !== null}
          data-testid="clear-all-btn"
          className="w-full h-12 rounded-2xl bg-orange text-white font-jakarta font-extrabold text-base flex items-center justify-center gap-2 active:opacity-80 disabled:opacity-50 transition-opacity"
        >
          {clearingAll ? (
            <>
              <CircleNotch size={16} weight="bold" className="animate-spin" />
              Clearing…
            </>
          ) : (
            <>
              <Trash size={16} weight="duotone" />
              Clear everything on this device
            </>
          )}
        </button>
      </div>
    </div>
  );
}
